/**
 * DocumentTree — the DMS explorer tree in the library's left rail.
 *
 * Mirrors the standard DMS hierarchy end to end:
 *
 *   Company → Counterparty type code → Counterparty code → Contract number →
 *   Draft / Signed / Supporting Documents (+ user sub-folders) → files
 *
 * Folder *names* stay the stable system codes; the human-readable name rides
 * alongside as a muted suffix and in the tooltip, so renaming a counterparty
 * or a contract never moves anything.
 *
 * Nothing below a counterparty is fetched until that node is expanded: its
 * contracts arrive a page at a time (with a "load more" row), and a contract's
 * contents the first time its own node opens. Everything is cached
 * afterwards, so opening the page costs one request no matter how many
 * contracts the tenant has — the tree once received every contract folder up
 * front and rendered all of them, which on a large tenant froze the browser.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiHome, FiBriefcase, FiUsers, FiFolder, FiFileText, FiChevronRight, FiLoader, FiTag, FiMoreHorizontal,
} from 'react-icons/fi';
import { documentLibraryAPI } from '../../services/api';

const SYSTEM_FOLDER_ORDER = ['draft', 'signed', 'supporting'];
// Contracts fetched per expansion of a counterparty node.
const CONTRACT_PAGE = 50;

// Rank system folders first, in their canonical order, then user folders A→Z.
function sortSubFolders(list, nameOf) {
  const rank = (f) => (f.systemKey ? SYSTEM_FOLDER_ORDER.indexOf(f.systemKey) : SYSTEM_FOLDER_ORDER.length);
  return [...list].sort((a, b) => (rank(a) - rank(b)) || nameOf(a).localeCompare(nameOf(b)));
}

/**
 * One row. Everything is a button so the whole tree is keyboard reachable;
 * the twisty is a separate button so expanding never forces a navigation.
 */
function TreeRow({
  depth, icon: Icon, label, suffix, count, title,
  expandable, expanded, loading, active, onToggle, onSelect,
}) {
  return (
    <div
      className={`doctree__row${active ? ' doctree__row--active' : ''}`}
      style={{ '--depth': depth }}
    >
      {expandable ? (
        <button
          type="button"
          className={`doctree__twisty${expanded ? ' doctree__twisty--open' : ''}`}
          onClick={onToggle}
          aria-label={label}
          aria-expanded={expanded}
          tabIndex={-1}
        >
          {loading ? <FiLoader className="doctree__spin" /> : <FiChevronRight />}
        </button>
      ) : (
        <span className="doctree__twisty doctree__twisty--empty" aria-hidden="true" />
      )}

      <button type="button" className="doctree__label" onClick={onSelect} title={title || label}>
        <Icon className="doctree__icon" />
        <span className="doctree__name">{label}</span>
        {suffix && <span className="doctree__suffix">{suffix}</span>}
      </button>

      {count != null && <span className="doctree__count">{count}</span>}
    </div>
  );
}

export default function DocumentTree({
  companyGroups,
  counterpartyTypeGroups,
  counterpartyGroups,
  standaloneList,
  loading,
  activeKey,
  openPath,
  // The contract folder open in the main pane, if any. It may not be on the
  // page of its counterparty the tree has loaded (or the tree may not have
  // loaded that counterparty at all); it is shown regardless so the sidebar
  // always reflects where the user is.
  pinnedContract,
  onOpenRoot,
  onOpenCompany,
  onOpenCounterpartyType,
  onOpenCounterparty,
  onOpenContract,
  onOpenSubFolder,
  onOpenFile,
  subfolderName,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(() => new Set());
  // contractId → { folders, documents } and folderId → { folders, files }
  const [contents, setContents] = useState(() => new Map());
  // counterparty group key → { items, total, page, loading, failed }
  const [contractPages, setContractPages] = useState(() => new Map());
  const [busy, setBusy] = useState(() => new Set());

  const nameOf = useCallback((f) => subfolderName(f, t) || '', [subfolderName, t]);

  const setFlag = (setter, key, on) => setter((prev) => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });

  // Fetch a node's children once, then serve from cache.
  const ensureLoaded = useCallback(async (cacheKey, request) => {
    if (contents.has(cacheKey)) return;
    setFlag(setBusy, cacheKey, true);
    try {
      const res = await request();
      setContents((prev) => new Map(prev).set(cacheKey, {
        folders: res.data.folders || [],
        files: res.data.documents || res.data.files || [],
      }));
    } catch {
      // Leave it uncached so expanding again retries.
      setContents((prev) => new Map(prev).set(cacheKey, { folders: [], files: [], failed: true }));
    } finally {
      setFlag(setBusy, cacheKey, false);
    }
  }, [contents]);

  // Fetch one page of a counterparty's contracts and append it. The first
  // page is requested on expand; later ones by the "load more" row.
  const contractPagesRef = useRef(contractPages);
  contractPagesRef.current = contractPages;
  const loadContractPage = useCallback(async (group, page) => {
    const key = group.key;
    const current = contractPagesRef.current.get(key);
    if (current && (current.loading || (page > 1 && current.page >= page))) return;
    setContractPages((prev) => new Map(prev).set(key, {
      items: current ? current.items : [],
      total: current ? current.total : group.contractCount || 0,
      page: current ? current.page : 0,
      loading: true,
      failed: false,
    }));
    try {
      const res = await documentLibraryAPI.listContractFolders({
        companyKey: group.companyKey,
        counterpartyCode: group.code || '',
        page,
        limit: CONTRACT_PAGE,
        sort: 'name',
      });
      const folders = res.data.folders || [];
      const total = res.data.pagination ? res.data.pagination.total : folders.length;
      setContractPages((prev) => {
        const cur = prev.get(key) || { items: [] };
        const seen = new Set(cur.items.map((f) => f.contractId));
        const items = page === 1 ? folders : [...cur.items, ...folders.filter((f) => !seen.has(f.contractId))];
        return new Map(prev).set(key, { items, total, page, loading: false, failed: false });
      });
    } catch {
      setContractPages((prev) => {
        const cur = prev.get(key) || { items: [], total: 0, page: 0 };
        return new Map(prev).set(key, { ...cur, loading: false, failed: true });
      });
    }
  }, []);

  const ensureContracts = useCallback((group) => {
    const cur = contractPagesRef.current.get(group.key);
    if (!cur || (cur.failed && !cur.loading)) loadContractPage(group, 1);
  }, [loadContractPage]);

  // Follow the main pane: when it drills into a level, expand the branch that
  // leads there and pull in any space along the way. Applied only when the
  // path actually changes, so manually collapsing a node it already opened
  // is not immediately undone.
  const appliedPathRef = useRef('');
  useEffect(() => {
    const keys = openPath || [];
    const sig = keys.join('|');
    if (sig === appliedPathRef.current) return;
    appliedPathRef.current = sig;
    if (!keys.length) return;

    setExpanded((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });

    for (const k of keys) {
      if (k.startsWith('cp:')) {
        const group = counterpartyGroups.find((g) => `cp:${g.key}` === k);
        if (group) ensureContracts(group);
      } else if (k.startsWith('ct:')) {
        const id = k.slice(3);
        ensureLoaded(id, () => documentLibraryAPI.getFolder(id));
      } else if (k.startsWith('sa:')) {
        const id = k.slice(3);
        ensureLoaded(id, () => documentLibraryAPI.getStandaloneFolder(id));
      }
    }
  }, [openPath, counterpartyGroups, ensureContracts, ensureLoaded]);

  const toggle = useCallback((key, load) => {
    const isOpen = expanded.has(key);
    setFlag(setExpanded, key, !isOpen);
    if (!isOpen && load) load();
  }, [expanded]);

  const toggleCounterparty = useCallback((g) => {
    toggle(`cp:${g.key}`, () => ensureContracts(g));
  }, [toggle, ensureContracts]);

  const toggleContract = useCallback((f) => {
    toggle(`ct:${f.contractId}`, () => ensureLoaded(f.contractId, () => documentLibraryAPI.getFolder(f.contractId)));
  }, [toggle, ensureLoaded]);

  const toggleStandalone = useCallback((f) => {
    const key = `sa:${f.folderId}`;
    toggle(key, () => ensureLoaded(f.folderId, () => documentLibraryAPI.getStandaloneFolder(f.folderId)));
  }, [toggle, ensureLoaded]);

  // Counterparty groups belonging to one counterparty type (per company).
  const counterpartiesByType = useMemo(() => {
    const map = new Map();
    for (const g of counterpartyGroups) {
      const key = g.counterpartyTypeKey;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    }
    return map;
  }, [counterpartyGroups]);

  // Counterparty type groups belonging to one company.
  const typesByCompany = useMemo(() => {
    const map = new Map();
    for (const g of counterpartyTypeGroups) {
      const key = g.companyKey;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(g);
    }
    return map;
  }, [counterpartyTypeGroups]);

  // The contracts shown under a counterparty: the loaded pages, with the
  // open contract pinned on top if it is not among them.
  const contractsFor = (g) => {
    const pageState = contractPages.get(g.key);
    const items = pageState ? pageState.items : [];
    const pinnedKey = pinnedContract && !pinnedContract.folderId
      ? `${pinnedContract.companyId || '__none'}|${pinnedContract.counterpartyCode || '__none'}`
      : null;
    if (pinnedKey === g.key && !items.some((f) => f.contractId === pinnedContract.contractId)) {
      return [pinnedContract, ...items];
    }
    return items;
  };

  // Files + folders nested under a given parent inside a loaded space.
  const childFoldersOf = (space, parentId) =>
    (space?.folders || []).filter((f) => String(f.parentId || '') === String(parentId || ''));
  const filesOf = (space, folderId) =>
    (space?.files || []).filter((d) => String(d.folderId || '') === String(folderId || ''));

  // Recursive: sub-folders of a contract/standalone space and their files.
  const renderFolderNode = (space, folder, depth, root, chain = []) => {
    const spaceKey = root.contractId || root.folderId;
    const key = `f:${spaceKey}:${folder._id}`;
    const trail = [...chain, folder];
    const isOpen = expanded.has(key);
    const kids = childFoldersOf(space, folder._id);
    const files = filesOf(space, folder._id);
    const label = nameOf(folder);

    return (
      <React.Fragment key={key}>
        <TreeRow
          depth={depth}
          icon={FiFolder}
          label={label}
          count={files.length || null}
          expandable={kids.length > 0 || files.length > 0}
          expanded={isOpen}
          active={activeKey === key}
          onToggle={() => setFlag(setExpanded, key, !isOpen)}
          onSelect={() => onOpenSubFolder(root, trail)}
        />
        {isOpen && (
          <>
            {sortSubFolders(kids, nameOf).map((c) => renderFolderNode(space, c, depth + 1, root, trail))}
            {files.map((d) => (
              <TreeRow
                key={`d:${d._id}`}
                depth={depth + 1}
                icon={FiFileText}
                label={d.name || d.fileName || d.title || t('documentLibrary.untitled', { defaultValue: 'Untitled' })}
                onSelect={() => onOpenFile(d, root, trail)}
              />
            ))}
          </>
        )}
      </React.Fragment>
    );
  };

  const renderContract = (f) => {
    const ctKey = `ct:${f.contractId}`;
    const ctOpen = expanded.has(ctKey);
    const space = contents.get(f.contractId);
    const roots = sortSubFolders(childFoldersOf(space, null), nameOf);
    const looseFiles = filesOf(space, null);
    return (
      <React.Fragment key={ctKey}>
        <TreeRow
          depth={3}
          icon={FiFolder}
          label={f.contractNumber || f.contractName}
          suffix={f.contractNumber ? f.contractName : null}
          title={f.contractNumber ? `${f.contractNumber} — ${f.contractName}` : f.contractName}
          count={f.documentCount || 0}
          expandable
          expanded={ctOpen}
          loading={busy.has(f.contractId)}
          active={activeKey === ctKey}
          onToggle={() => toggleContract(f)}
          onSelect={() => onOpenContract(f)}
        />
        {ctOpen && space && (
          <>
            {roots.map((sf) => renderFolderNode(space, sf, 4, f))}
            {looseFiles.map((d) => (
              <TreeRow
                key={`d:${d._id}`}
                depth={4}
                icon={FiFileText}
                label={d.name || d.fileName || d.title}
                onSelect={() => onOpenFile(d, f, [])}
              />
            ))}
          </>
        )}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <nav className="doctree">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="doctree__row doctree__row--skeleton" aria-hidden="true">
            <div className="shimmer shimmer--leaf" />
            <div className="shimmer shimmer--line shimmer--w-60" />
          </div>
        ))}
      </nav>
    );
  }

  return (
    <nav className="doctree">
      <TreeRow
        depth={0}
        icon={FiHome}
        label={t('documentLibrary.allFolders', { defaultValue: 'All folders' })}
        count={companyGroups.length + standaloneList.length}
        active={activeKey === '__root'}
        onSelect={onOpenRoot}
      />

      {companyGroups.length > 0 && (
        <div className="doctree__section">{t('documentLibrary.companyFolder', { defaultValue: 'Company' })}</div>
      )}

      {companyGroups.map((c) => {
        const coKey = `co:${c.key}`;
        const coOpen = expanded.has(coKey);
        const types = typesByCompany.get(c.key) || [];
        return (
          <React.Fragment key={coKey}>
            <TreeRow
              depth={0}
              icon={FiBriefcase}
              label={c.label}
              count={c.documentCount || 0}
              expandable={types.length > 0}
              expanded={coOpen}
              active={activeKey === coKey}
              onToggle={() => setFlag(setExpanded, coKey, !coOpen)}
              onSelect={() => onOpenCompany(c)}
            />

            {coOpen && types.map((tg) => {
              const cptKey = `cpt:${tg.key}`;
              const cptOpen = expanded.has(cptKey);
              const cps = counterpartiesByType.get(tg.key) || [];
              return (
                <React.Fragment key={cptKey}>
                  <TreeRow
                    depth={1}
                    icon={FiTag}
                    label={tg.label}
                    // Code is the folder identity; the localized type name rides
                    // alongside it.
                    suffix={tg.description || null}
                    title={tg.description ? `${tg.label} — ${tg.description}` : tg.label}
                    count={tg.counterpartyCount || 0}
                    expandable={cps.length > 0}
                    expanded={cptOpen}
                    active={activeKey === cptKey}
                    onToggle={() => setFlag(setExpanded, cptKey, !cptOpen)}
                    onSelect={() => onOpenCounterpartyType(tg)}
                  />

                  {cptOpen && cps.map((g) => {
                    const cpKey = `cp:${g.key}`;
                    const cpOpen = expanded.has(cpKey);
                    const pageState = contractPages.get(g.key);
                    const list = contractsFor(g);
                    const hasMore = pageState && !pageState.loading && pageState.items.length < pageState.total;
                    return (
                      <React.Fragment key={cpKey}>
                        <TreeRow
                          depth={2}
                          icon={FiUsers}
                          label={g.label}
                          // Code is the folder identity; the name rides alongside it.
                          suffix={g.description || null}
                          title={g.description ? `${g.label} — ${g.description}` : g.label}
                          count={g.contractCount || 0}
                          expandable={(g.contractCount || 0) > 0}
                          expanded={cpOpen}
                          loading={!!(pageState && pageState.loading && !pageState.items.length)}
                          active={activeKey === cpKey}
                          onToggle={() => toggleCounterparty(g)}
                          onSelect={() => onOpenCounterparty(g)}
                        />

                        {cpOpen && list.map(renderContract)}

                        {cpOpen && hasMore && (
                          <TreeRow
                            depth={3}
                            icon={FiMoreHorizontal}
                            label={t('documentLibrary.loadMore', { defaultValue: 'Load more' })}
                            suffix={`${pageState.items.length} / ${pageState.total}`}
                            onSelect={() => loadContractPage(g, pageState.page + 1)}
                          />
                        )}
                        {cpOpen && pageState && pageState.loading && pageState.items.length > 0 && (
                          <TreeRow depth={3} icon={FiLoader} label="…" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}

      {standaloneList.length > 0 && (
        <>
          <div className="doctree__section">{t('documentLibrary.folders')}</div>
          {standaloneList.map((f) => {
            const key = `sa:${f.folderId}`;
            const isOpen = expanded.has(key);
            const space = contents.get(f.folderId);
            // The root folder itself comes back inside `folders`; its children
            // hang off its own id, not null.
            const roots = sortSubFolders(childFoldersOf(space, f.folderId), nameOf);
            const files = filesOf(space, f.folderId);
            return (
              <React.Fragment key={key}>
                <TreeRow
                  depth={0}
                  icon={FiFolder}
                  label={f.name}
                  count={f.documentCount || 0}
                  expandable
                  expanded={isOpen}
                  loading={busy.has(f.folderId)}
                  active={activeKey === key}
                  onToggle={() => toggleStandalone(f)}
                  onSelect={() => onOpenContract(f)}
                />
                {isOpen && space && (
                  <>
                    {roots.map((sf) => renderFolderNode(space, sf, 1, f))}
                    {files.map((d) => (
                      <TreeRow
                        key={`d:${d._id}`}
                        depth={1}
                        icon={FiFileText}
                        label={d.name || d.fileName}
                        onSelect={() => onOpenFile(d, f, [])}
                      />
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
    </nav>
  );
}
